import React from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Ceo from '../../assets/images/ceo.jpg';
import GalleryLoungs from './GalleryLoungs';
import MenuServices from './MenuServices';
import './ServicePage.css';

const ServicePageLounges = () => {
  // Composant de particules pour lounge VIP
  const ParticlesBackground = () => (
    <div className="particles-background-service">
      {[...Array(35)].map((_, i) => (
        <div 
          key={i}
          className="particle-service" 
          style={{
            '--i': i,
            '--random-x': Math.random() * 100,
            '--random-y': Math.random() * 100,
            '--random-size': Math.random() * 4 + 1,
            '--random-duration': Math.random() * 25 + 20,
            '--random-delay': Math.random() * -30,
            '--random-color': `hsl(${Math.random() * 30 + 40}, 70%, 60%)`
          }}
        />
      ))}
      {/* Particules spéciales pour lounge luxe */}
      {[...Array(12)].map((_, i) => (
        <div 
          key={`lounge-${i}`}
          className="particle-service lounge-particle"
          style={{
            '--i': i + 35,
            '--random-x': Math.random() * 100,
            '--random-y': Math.random() * 100,
            '--random-duration': Math.random() * 40 + 30,
            '--random-delay': Math.random() * -50
          }}
        />
      ))}
    </div>
  );

  // Animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.8
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 20,
        duration: 0.8
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.15,
        duration: 0.6,
        ease: "easeOut"
      }
    }),
    hover: {
      scale: 1.03,
      x: 12,
      backgroundColor: "rgba(245, 158, 11, 0.1)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 12
      }
    }
  };

  const activities = [
    {
      title: "Accès à un espace lounge haut de gamme",
      description: "Ambiance luxueuse, design élégant, confort absolu.",
      icon: "🛋️"
    },
    {
      title: "Services de restauration et boissons premium",
      description: "Sélection de mets raffinés, cafés, thés et rafraîchissements exclusifs.",
      icon: "🍾"
    },
    {
      title: "Espaces de travail privés et connectés",
      description: "Connexion Wi-Fi rapide, bureaux équipés, prises de charge et imprimantes.",
      icon: "💻"
    },
    {
      title: "Ambiance calme et détente avant le vol",
      description: "Zones de repos, lecture, musique douce et environnement apaisant.",
      icon: "🎵"
    },
    {
      title: "Assistance personnalisée et discrète",
      description: "Équipe dédiée pour répondre à vos besoins en toute confidentialité.",
      icon: "🤵"
    }
  ];

  return (
    <section id="main-container" className="main-container-service lounge-page">
      <ParticlesBackground />
      
      <div className="container">
        <div className="row">
          {/* Sidebar avec MenuServices */}
          <div className="col-xl-3 col-lg-4">
            <div className="sidebar sidebar-left">
              <MenuServices />

              {/* Widget CEO avec animations */}
              <motion.div 
                className="widget"
                variants={cardVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="quote-item quote-border glass-card lounge-card">
                  <motion.div 
                    className="quote-text-border"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                  >
                    Parce que vous méritez le meilleur, simplement.
                  </motion.div>

                  <div className="quote-item-footer">
                    <motion.img 
                      className="testimonial-thumb"
                      src={Ceo} 
                      alt="Ndagbia Mobutu - CEO Azyekos Services"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1, type: "spring" }}
                    />
                    <div className="quote-item-info">
                      <motion.h3 
                        className="quote-author"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.2 }}
                      >
                        Ndagbia Mobutu
                      </motion.h3>
                      <motion.span 
                        className="quote-subtext"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.4 }}
                      >
                        CEO, Azyekos Services
                      </motion.span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="col-xl-8 col-lg-8">
            <motion.div 
              className="content-inner-page"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Titre principal avec animation */}
              <motion.div
                className="title-section lounge-title"
                variants={cardVariants}
              >
                <motion.h2
                  className="column-title mrt-0 fw-bold lounge-main-title"
                  initial={{ opacity: 0, y: -50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, type: "spring", stiffness: 70 }}
                >
                  Lounge VIP & Espace de Travail
                </motion.h2>
                <motion.div 
                  className="title-underline lounge-underline"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                />
              </motion.div>

              {/* Introduction */}
              <motion.div 
                className="row"
                variants={containerVariants}
              >
                <div className="col-md-12">
                  <motion.div
                    className="text-content lounge-text"
                    variants={textVariants}
                  >
                    <motion.p
                      className="text-left elegant-text lounge-paragraph"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.8 }}
                    >
                      Profitez d'un Lounge VIP raffiné, combinant confort, élégance et espace de travail, pour optimiser vos instants avant chaque vol. Chez Azyekos, nous vous offrons bien plus qu'une simple salle d'attente : un véritable havre de paix au cœur de l'aéroport.
                    </motion.p>
                    <motion.p
                      className="text-left elegant-text lounge-paragraph"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8, duration: 0.8 }}
                    >
                      Pensé pour répondre aux besoins des voyageurs exigeants, notre Lounge VIP vous permet de rester connecté tout en profitant d'un cadre apaisant et inspirant. Qu'il s'agisse d'un voyage d'affaires ou d'une évasion personnelle, cet espace exclusif vous garantit une expérience premium avant l'embarquement.
                    </motion.p>
                  </motion.div>
                </div>
              </motion.div>

              <div className="gap-40"></div>

              {/* Galerie */}
              <motion.div
                id="page-slider"
                className="page-slider lounge-gallery"
                variants={cardVariants}
              >
                <GalleryLoungs />
              </motion.div>

              <div className="gap-40"></div>

              {/* Liste des activités */}
              <motion.div 
                className="row"
                variants={containerVariants}
              >
                <div className="col-md-12">
                  <motion.h3
                    className="activities-title lounge-activities-title"
                    variants={textVariants}
                  >
                    Voici 5 activités clés du service Lounge VIP d'Azyekos :
                  </motion.h3>
                  
                  <div className="activities-list lounge-activities">
                    {activities.map((activity, index) => (
                      <motion.div
                        key={index}
                        className="activity-item lounge-activity"
                        custom={index}
                        variants={listItemVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover="hover"
                      >
                        <div className="activity-icon lounge-icon">
                          <motion.div
                            className="icon-circle lounge-icon-circle"
                            whileHover={{ 
                              rotate: 360,
                              scale: 1.1
                            }}
                            transition={{ duration: 0.6 }}
                          >
                            {activity.icon}
                          </motion.div>
                        </div>
                        <div className="activity-content lounge-activity-content">
                          <motion.h4 
                            className="activity-title lounge-activity-title"
                            whileHover={{ color: "#f59e0b" }}
                            transition={{ duration: 0.3 }}
                          >
                            {activity.title}
                          </motion.h4>
                          <motion.p 
                            className="activity-description lounge-activity-description"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: index * 0.15 + 0.5 }}
                          >
                            {activity.description}
                          </motion.p>
                        </div>
                        <motion.div 
                          className="activity-glow"
                          initial={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>

              <div className="gap-40"></div>

              {/* Call to Action */}
              <motion.div 
                className="call-to-action modern-cta lounge-cta"
                variants={cardVariants}
              >
                <div className="row align-items-center">
                  <div className="col-md-8 text-center text-md-left">
                    <motion.div
                      className="call-to-action-text"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 }}
                    >
                      <h4 className="action-title lounge-action-title">
                        Prêt pour une expérience lounge exclusive ?
                      </h4>
                      <p className="action-subtitle lounge-action-subtitle">
                        Réservez votre accès VIP et transformez votre attente en moment de luxe
                      </p>
                    </motion.div>
                  </div>
                  <div className="col-md-4 text-center text-md-right mt-3 mt-md-0">
                    <motion.div
                      className="call-to-action-btn"
                      whileHover={{ 
                        scale: 1.05,
                        transition: { type: "spring", stiffness: 300 }
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link to="/Contact" className="btn btn-premium lounge-btn">
                        <span className="btn-text">Contactez-nous</span>
                        <motion.div 
                          className="btn-arrow"
                          initial={{ x: 0 }}
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 400 }}
                        >
                          →
                        </motion.div>
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicePageLounges;